// ADR 6.10.26a §1 — an install button that does not wait for consent.
//
// The install strip shares one slot with the consent strip and waits for it
// (30.9.26l), and web consent defaults to denied, so most visitors never
// answered and never saw the strip. This is `InstallEntry`'s logic in a
// button's shape, mounted where every visitor passes: the signed-in header
// (`header`) and the sign-in / register door (`door`).
//
// Reads **no consent**, and is **not dismissible**: it is chrome, not an
// invitation, so it goes only when there is nothing to install (`none`). The
// strip's dismissal key does not hide it. A text button with the existing
// "Install app" label and no icon, the same at every window class; the steps
// open as a panel absolutely positioned under it, so opening them never
// reflows the header row or the door. It does not open an `?install=1`
// request: the top strip slot is that request's one consumer (§5).
//
// Must not import the wasm facade: `Header` and the doors are in 25.9.26f's
// boot graph.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { InstallBoundary } from '../telemetry/field/InstallBoundary'
import { emitInstallEntryClicked } from '../telemetry/installStrip'
import { Button } from '../ui/Button'
import { InstallPanel } from './InstallHandoff'
import { promptInstall } from './installPrompt'
import { useInstallAffordance } from './useInstallAffordance'

interface InstallButtonProps {
  surface: 'header' | 'door'
  /** The wrapper's placement in its parent (the door's grid span). */
  className?: string
}

/** ADR 15.10.26d §2 — a render throw here costs the button, not the page. */
export function InstallButton(props: InstallButtonProps) {
  return (
    <InstallBoundary surface={props.surface}>
      <InstallButtonBody {...props} />
    </InstallBoundary>
  )
}

function InstallButtonBody({ surface, className = '' }: InstallButtonProps) {
  const { t } = useTranslation()
  const state = useInstallAffordance()
  const [stepsOpen, setStepsOpen] = useState(false)
  if (state.affordance === 'none') return null

  const onClick = () => {
    emitInstallEntryClicked(surface, state)
    if (state.affordance === 'prompt') void promptInstall(surface)
    else setStepsOpen((open) => !open)
  }
  return (
    <div className={`relative${className === '' ? '' : ` ${className}`}`} data-testid={`install-button-${surface}`}>
      <Button variant="secondary" onClick={onClick}>
        {t('pwa_install_app')}
      </Button>
      {stepsOpen && state.affordance !== 'prompt' && (
        <div className="absolute end-0 top-full z-20 mt-1">
          <InstallPanel state={state} onClose={() => setStepsOpen(false)} />
        </div>
      )}
    </div>
  )
}
